import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property, queryAssignedElements } from 'lit/decorators.js'
import {
	EMPTY,
	Observable,
	catchError,
	distinctUntilChanged,
	filter,
	finalize,
	forkJoin,
	from,
	fromEvent,
	map,
	merge,
	of,
	Subject,
	switchMap,
	tap,
	takeUntil,
} from 'rxjs'
import { area } from './area.service'
import { RouteComponent, SchmancyRoute } from './route.component'
import { ActiveRoute, HISTORY_STRATEGY, RouteAction } from './router.types'
import { buildPathFromPattern, isDynamicRoutePattern, matchPathPattern, matchPathPrefix, patternParamNames } from './utils'

// How many guards may refuse back-to-back before the area calls it a loop. A
// refusal that redirects into another refusal is a chain, and a mutually
// refusing pair is an unbounded one. Unbounded with synchronous guards means
// each hop nests on the stack: measured at 44 redirects before the browser
// raised a RangeError, which the per-navigation catchError below then
// swallowed — so the pair failed silently with nothing mounted.
const MAX_REDIRECT_CHAIN = 10

@customElement('schmancy-area')
export class SchmancyArea extends SchmancyElement {
	// One cell, every page in it. A single page fills the host exactly as it did
	// under flex-column; two pages during a swap OVERLAP instead of stacking, and
	// — the point — both are sized by the same track, so they cannot disagree
	// about their geometry for even one frame while the host resizes underneath
	// them (a collapsing nav rail, a rotating device). Pinning the outgoing page
	// to its measured pixels instead opens a bare strip along whichever edge the
	// host grows toward, which reads as a hard seam sweeping across the swap.
	// `schmancy-route` is display:none, so declared routes claim no cell.
	// `minmax(0, 1fr)`, not `1fr`: a page whose host is `display: contents`
	// generates no box, its first shadow child becomes the grid item, and the
	// `::slotted` min-size below never reaches it. A bare `1fr` would then grow
	// to that item's content and push a long page past the area, where nothing
	// can scroll to it.
	static styles = [
		css`
			:host {
				position: relative;
				display: grid;
				grid-template-columns: minmax(0, 1fr);
				grid-template-rows: minmax(0, 1fr);
				flex: 1 1 0;
				height: 100%;
				width: 100%;
				min-height: 0;
				min-width: 0;
				contain: layout style;
			}
			::slotted(:not(schmancy-route)) {
				box-sizing: border-box;
				grid-area: 1 / 1;
				min-height: 0;
				min-width: 0;
			}
		`,
	]

	@property() name!: string

	@property() default!: RouteComponent

	/**
	 * The address above this area, as a path pattern (`/o/:orgId/admin`). The area
	 * reads only the segments after it. Unset, a nested area reads the segments
	 * after its parent area's route, and an area with no parent reads the whole path.
	 */
	@property() base?: string

	@queryAssignedElements({ selector: 'schmancy-route', flatten: true })
	private routes!: SchmancyRoute[]

	/**
	 * The page currently standing in this area, under the identity that put it
	 * there: its route and state, and the path some navigation named for it —
	 * null until one does. Not reactive: it records what the DOM already holds so
	 * the next navigation can tell "a different page" from "the same page at a
	 * different address", and rendering never reads it.
	 */
	private mounted: { pageKey: string; pagePath: string | null; element: HTMLElement } | null = null

	/**
	 * The route this area starts at, spoken only once the pipeline below is
	 * listening on every arm. It is a navigation like any other and can be
	 * refused like any other, and a refusal answers itself through
	 * `area.request` — so that arm has to be subscribed before this one speaks.
	 */
	private readonly startup = new Subject<RouteAction>()

	/** Guards that have refused since the last route actually landed. */
	private redirectChain = 0

	protected firstUpdated(): void {
		if (!this.name) throw new Error('Area name is required')

		merge(
			this.startup,

			// Live arm: future-only Subject — post-mount pushes and pop() clears.
			area.request.pipe(filter(({ area }) => area === this.name)),

			// Every action this arm emits is marked `_source: 'browser'`: the browser has
			// already restored both the address and the state of the entry it moved to, so
			// the commit below records the navigation without writing history over it.
			fromEvent<PopStateEvent>(window, 'popstate').pipe(
				tap(() => area._publishURL()),
				map(event => {
					const sameDocument = area._popstateMatchesCommittedDocument(event)
					if (history.state?.schmancyAreas?.[this.name]) {
						const s = history.state.schmancyAreas[this.name]
						const declaredIdentity =
							typeof s.when === 'string' && this.routeNamed(s.when) ? s.when : s.component
						return {
							area: this.name,
							component: declaredIdentity,
							state: s.state || {},
							params: s.params || {},
							props: s.props || {},
							// The address the push that wrote this entry named, if any —
							// never `location`, which a pathless push derived from its params.
							path: typeof s.path === 'string' ? s.path : undefined,
							historyStrategy: HISTORY_STRATEGY.pop,
							_source: 'browser',
							_sameDocument: sameDocument,
						} as RouteAction
					}
					// Every arm below resolves an entry the router never wrote from its
					// address alone, so that address is also the path that names its page.
					if (sameDocument) return null
					const path = location.pathname
					const lastSegment = path.split('/').pop() || ''
					if (lastSegment && (lastSegment.includes('{') || lastSegment.includes('%7B'))) {
						try {
							const parsed = JSON.parse(decodeURIComponent(lastSegment))
							if (typeof parsed === 'object' && parsed !== null && parsed[this.name]) {
								const r = parsed[this.name]
								return {
									area: this.name,
									component: typeof r.when === 'string' && this.routeNamed(r.when) ? r.when : r.component,
									state: r.state || {},
									params: r.params || {},
									props: r.props || {},
									historyStrategy: HISTORY_STRATEGY.replace,
									_source: 'browser',
									path,
									_sameDocument: sameDocument,
								} as RouteAction
							}
						} catch {}
					}
					// Reached only for an entry carrying no schmancyAreas of its own —
					// one a foreign pushState minted, or one from before this document
					// loaded. Every entry this router wrote returns above, from state
					// that is strictly richer than the address: it carries the props and
					// the route state the URL never held.
					const addressed = this.routeAt(path)
					if (addressed) {
						return {
							area: this.name,
							component: addressed.when,
							state: {},
							params: addressed.params,
							historyStrategy: HISTORY_STRATEGY.silent,
							_source: 'browser',
							path,
							_sameDocument: sameDocument,
						} as RouteAction
					}
					return this.default
						? ({
								area: this.name,
								component: this.default,
								state: {},
								params: {},
								historyStrategy: HISTORY_STRATEGY.silent,
								_source: 'browser',
								path,
								_sameDocument: sameDocument,
							} as RouteAction)
						: null
				}),
			),
		)
			.pipe(
				filter((r): r is RouteAction => r !== null),
				filter(action => {
					if (action._source !== 'browser') return true
					if (action._sameDocument !== true) return true
					if (typeof action.component !== 'string') return true

					const active = area.current.get(this.name)
					if (!active) return true
					return (
						`${action.component}${JSON.stringify(action.params || {})}${JSON.stringify(action.state || {})}${JSON.stringify(action.props || {})}` !==
						`${active.when ?? active.component}${JSON.stringify(active.params || {})}${JSON.stringify(active.state || {})}${JSON.stringify(active.props || {})}`
					)
				}),

				// Per-navigation error isolation — one bad nav skips, outer stream keeps flowing
				switchMap(action =>
					of(action).pipe(
						// A stale route name (e.g. a pre-rename tag baked into a shared/bookmarked
						// URL) matches no declared route's `when` and isn't itself a registered
						// custom element — falls back to `.default` here, before matchedRoute
						// resolution, so it flows through the same guard/lazy handling the default
						// takes at boot. Areas with no declared routes are untouched — they rely on
						// createElement + late upgrade for tag-push apps.
						map(action => {
							if (
								typeof action.component === 'string' &&
								this.routes?.length &&
								!this.routeNamed(action.component) &&
								!customElements.get(action.component) &&
								this.default
							) {
								console.warn(
									`[${this.name}] No route matches "${action.component}" and no such custom element is registered — falling back to default.`,
								)
								return { ...action, component: this.default }
							}
							return action
						}),
						switchMap(action => {
							let component: RouteComponent | null = action.component
							let matchedRoute: SchmancyRoute | undefined

							if (typeof component === 'string' && this.routes) {
								matchedRoute = this.routeNamed(component)
								if (matchedRoute) component = matchedRoute.component
							}
							if (!matchedRoute && component instanceof HTMLElement && this.routes) {
								matchedRoute = this.routes.find(r => r.when === component.tagName.toLowerCase())
							}

							if (!matchedRoute && typeof component === 'function' && 'preload' in component) {
								return from(
									(component as () => Promise<{ default: CustomElementConstructor }>)().then(m => ({
										...action,
										component: m.default as RouteComponent,
										matchedRoute:
											this.routes?.find(r => r.when === (customElements.getName(m.default) ?? '')) ?? undefined,
									})),
								)
							}
							return of({ ...action, component, matchedRoute })
						}),

						switchMap(carry => {
							if (!carry.matchedRoute?.guard) return of(carry)
							// A guard stays subscribed for as long as its navigation stands and
							// may answer again on every change it watches. A repeated verdict
							// is not a navigation: re-delivering this carry would commit the
							// route again and write its address over whatever another area has
							// written since. Only a changed verdict acts — a refusal redirects,
							// and an allowance after a refusal lands the route it held back.
							return carry.matchedRoute.guard.pipe(
								distinctUntilChanged(),
								switchMap(allowed => {
									if (allowed) return of(carry)
									// The redirect a refusal provokes is a navigation of its own, and
									// its target is free to refuse in turn. Cutting the chain at the
									// dispatch is what stops a mutually-refusing pair, and it reports
									// the pair; the count clears the moment any route lands.
									if (this.redirectChain >= MAX_REDIRECT_CHAIN) {
										this.redirectChain = 0
										console.error(
											`[${this.name}] Redirect loop: ${MAX_REDIRECT_CHAIN} guards refused in a row without a route landing. Last blocked: "${carry.matchedRoute?.when ?? 'unknown'}".`,
										)
										return EMPTY
									}
									this.redirectChain++
									carry.matchedRoute?.dispatchEvent(
										new CustomEvent('redirect', {
											detail: {
												blockedRoute: carry.matchedRoute?.when ?? 'unknown',
												area: this.name,
												params: carry.params || {},
												state: carry.state || {},
											},
											bubbles: true,
											composed: true,
										}),
									)
									return EMPTY
								}),
							)
						}),

						// A scoped area owns the path below its scope, so a pattern route pushed
						// there without a path is addressed as that scope plus the pattern filled
						// in, its declared params riding in the path rather than the query. A
						// param the address cannot carry leaves the push pathless, as before.
						map(carry => {
							const when = carry.matchedRoute?.when
							if (carry.path || carry._source === 'browser' || carry._source === 'initial' || !isDynamicRoutePattern(when))
								return carry
							const segments = location.pathname.split('/').filter(Boolean)
							const scope = this.scopeStart(segments)
							if (typeof scope !== 'number') return carry
							const built = buildPathFromPattern(when, carry.params)
							return built === null ? carry : { ...carry, path: `/${[...segments.slice(0, scope), built.slice(1)].join('/')}` }
						}),

						// Departure signal — a guard-blocked push returns EMPTY above and never
						// reaches here, so only a push that will actually land announces one.
						// Still synchronous (before the lazy import below) for the unguarded
						// path; a guarded route's departure fires only once its own guard
						// resolves truthy. Same/different compares `when` to `when` when both
						// sides declare one (the identity space known synchronously for a
						// lazy-loaded route), falling back to tag-to-tag otherwise; the reported
						// `to` still prefers a resolved tag when one is already known.
						map(carry => {
							const targetWhen = carry.matchedRoute?.when
							const c = carry.component
							const targetTag =
								typeof c === 'string'
									? c
									: c instanceof HTMLElement
										? c.tagName.toLowerCase()
										: typeof c === 'function' && !('preload' in c)
											? customElements.getName(c as CustomElementConstructor) || c.name || undefined
											: undefined
							const active = area.current.get(this.name)
							const departed =
								targetWhen && active?.when
									? targetWhen !== active.when
									: targetTag && active?.component
										? targetTag !== active.component
										: false
							const to = targetTag ?? targetWhen
							if (departed && active && to) {
								area.departureSubject.next({ area: this.name, from: active.component, to })
							}
							return carry
						}),

						// Lazy component loads after the guard check, not before — skip the
						// chunk fetch entirely when the guard is going to reject the route.
						switchMap(carry => {
							if (!(typeof carry.component === 'function' && 'preload' in carry.component)) return of(carry)
							return from(
								(carry.component as () => Promise<{ default: CustomElementConstructor }>)().then(m => ({
									...carry,
									component: m.default as RouteComponent,
								})),
							)
						}),

						map(carry => {
							const c = carry.component
							// The declared route name (`when`) and the custom element's registered
							// tag name are both stable across a minified production build; a
							// class's runtime `.name` is not — two unrelated lazy chunks can both
							// minify to the same short inferred name (e.g. `class extends t{}` →
							// `var i=class extends t{}` in every chunk that inlines to one local),
							// which previously collided this dedup key and silently swallowed the
							// navigation between them. `.name` is now the last resort, reached only
							// for a raw HTMLElement/constructor pushed with no matching declared route.
							const id =
								!c || c === ''
									? 'null'
									: carry.matchedRoute
										? carry.matchedRoute.when
										: typeof c === 'string'
											? c
											: c instanceof HTMLElement
												? c.tagName.toLowerCase()
												: typeof c === 'function'
													? customElements.getName(c as CustomElementConstructor) || c.name || 'CustomElement'
													: 'null'
							// Fragment entries share the route identity of their initial document;
							// state/params/props below still distinguish same-address route entries.
							// A traversal's destination is the address the browser restored.
							const destination = carry.path ? new URL(carry.path, location.origin) : null
							const destinationIdentity =
								carry._source === 'browser'
									? `${location.pathname}${location.search}`
									: destination
										? `${destination.pathname.replace(/\/+$/, '') || '/'}${destination.search}${destination.hash}`
										: ''
							// Two different questions, and folding them into one string was
							// the defect. `key` answers "is this push a no-op?", which every
							// field below belongs in. `pageKey` and `pagePath` answer "is this
							// a different PAGE?", and only three things make it one: the
							// resolved component, the path it sits at, and the history
							// entry's own state — a state-bearing entry is a distinct
							// restore, which `area.fragment-navigation.test.ts` pins.
							//
							// What is deliberately NOT here is the query string and the
							// params/props riding with it. Those name a position INSIDE the
							// page — a tab, a filter, a selected row — so moving them used
							// to destroy the page they were describing, and there was no way
							// to move the address without paying that. They are payload now:
							// assigned onto the page that is already standing there.
							//
							// The path is the one the navigation NAMED for its page: a push's
							// own `path`, the one a restored entry recorded for the push that
							// wrote it, or the address itself for an entry the router never
							// wrote and for the page a cold load mounted. A pathless push names
							// none, and neither does a Back to the entry it wrote, whose
							// address was derived from params — so `pagePath` is null there, and
							// a null path stays on whatever path the standing page sits at.
							const namedPath = carry._source === 'initial' ? location.pathname : destination?.pathname
							const pagePath = namedPath === undefined ? null : namedPath.replace(/\/+$/, '') || '/'
							// A push that clears a name still standing in the address, and
							// does not write it straight back, moves the address — a change
							// the key cannot see, so it must not read as a repeat. With history
							// mode off no push writes the address, so none moves it.
							const written = area.enableHistoryMode && carry.clearQueryParams
								? area._queryFor(
										this.name,
										{
											component: id,
											area: this.name,
											when: carry.matchedRoute?.when,
											state: carry.state || {},
											params: carry.params || {},
											props: carry.props || {},
										},
										carry.clearQueryParams,
										carry.path,
									)
								: null
							const clearsQuery =
								written !== null && [...new URLSearchParams(location.search).keys()].some(name => !written.has(name))
							return {
								...carry,
								clearsQuery,
								pageKey: `${id}${JSON.stringify(carry.state)}`,
								pagePath,
								key: `${id}${destinationIdentity}${JSON.stringify(carry.params)}${JSON.stringify(carry.state)}${JSON.stringify(carry.props)}`,
							}
						}),

						catchError(err => {
							console.error(`[${this.name}] Navigation error:`, err)
							return EMPTY
						}),
					),
				),

				distinctUntilChanged((previous, next) => previous.key === next.key && !next.clearsQuery),

				map(carry => {
					const c = carry.component
					// The address moved but it still addresses THIS page: assign the new
					// payload onto the element already standing there. What re-runs is
					// what re-runs for any other property write on a Lit element — the
					// element's own update — plus `area.params(name)`/`area.param(...)`
					// for a consumer reading the channel rather than a property. Nothing
					// re-runs that a `connectedCallback` owns, which is the whole point:
					// a half-typed field, a pending upload and the scroll position are
					// exactly the state a re-mount was destroying.
					const mounted = this.mounted
					const standing =
						mounted?.pageKey === carry.pageKey &&
						mounted.element.isConnected &&
						(mounted.pagePath === null || carry.pagePath === null || mounted.pagePath === carry.pagePath)
							? mounted.element
							: null
					if (standing) {
						// A navigation that names where the page sits moves the record there.
						if (carry.pagePath !== null) this.mounted = { ...mounted, pagePath: carry.pagePath }
						if (carry.params) Object.assign(standing, carry.params)
						if (carry.props) Object.assign(standing, carry.props)
						if (carry.state) Object.assign(standing, { state: carry.state })
						return { ...carry, element: standing, standing: true }
					}
					let element: HTMLElement | null = null
					if (typeof c === 'string' && c !== '') {
						try {
							element = document.createElement(c)
						} catch {
							console.error(`[${this.name}] Failed to create element:`, c)
						}
					} else if (c instanceof HTMLElement) {
						element = c
					} else if (typeof c === 'function') {
						try {
							element = new (c as CustomElementConstructor)()
						} catch (e) {
							console.error(`[${this.name}] Failed to instantiate:`, e)
						}
					}
					if (element) {
						if (carry.params) Object.assign(element, carry.params)
						if (carry.props) Object.assign(element, carry.props)
						if (carry.state) Object.assign(element, { state: carry.state })
					}
					return { ...carry, element, standing: false }
				}),

				// Commit is synchronous and unconditional; the swap below is a
				// cancellable switchMap, so a superseding navigation drops every stale
				// page and lands on its own element regardless of how this one ended.
				switchMap(carry => {
					const { element } = carry
					// Clears this swap's own dissolve styling so a caller-owned element
					// (pushed directly, re-pushed later) doesn't come back half-faded.
					const dropStale = () => {
						for (const el of Array.from(this.children)) {
							if (el instanceof SchmancyRoute || el === element) continue
							if (el instanceof HTMLElement) {
								el.style.zIndex = ''
								el.style.pointerEvents = ''
								el.style.willChange = ''
								el.style.opacity = ''
							}
							el.remove()
						}
					}

					// No component resolved — drop every mounted page, commit nothing.
					if (!element) {
						dropStale()
						return EMPTY
					}

					// Commit the navigation synchronously — area state and history track
					// the latest push immediately, never gated on the fade below.
					const activeRoute: ActiveRoute = {
						component: element.tagName.toLowerCase(),
						when: carry.matchedRoute?.when,
						state: carry.state || {},
						area: this.name,
						params: carry.params || {},
						props: carry.props || {},
					}
					this.redirectChain = 0
					area.current.set(this.name, activeRoute)
					area.$current.next(area.current)
					// A browser-sourced navigation is not written back. The entry the user
					// moved to already carries its own address and its own schmancyAreas —
					// regenerating either can only reproduce it or corrupt it, and it
					// corrupted it: a Back press inside an area whose pushes named their own
					// `path` re-derived the address from the areas map and replaced the
					// restored `/admin/exhibitions` with `/admin/exhibitions/%7B...%7D`, so
					// an app got a readable URL going forward and a blob coming back.
					if (area.enableHistoryMode && carry._source !== 'browser') {
						area.updateBrowserHistory(
							this.name,
							activeRoute,
							carry.historyStrategy ?? HISTORY_STRATEGY.push,
							carry.clearQueryParams,
							carry.path,
						)
					}
					area._commitAddress()

					// The page never left, so there is nothing to swap: no fade, no
					// re-append (appending a node that is already a child MOVES it, which
					// is a disconnect and a reconnect — the re-mount this whole path
					// exists to avoid), and above all no `scrollTop = 0`.
					if (carry.standing) {
						dropStale()
						// A swap superseded mid-fade converges the DOM through its own
						// `finalize`, which runs between the decision above and this line and
						// takes the standing page down with every other stale child. Putting
						// that same instance back is still the page this address named, and
						// it keeps everything the instance holds; leaving it out would leave
						// the area showing nothing at all.
						if (this.isConnected && !element.isConnected) this.appendChild(element)
						return EMPTY
					}

					const duration = carry.animationDuration ?? 150
					const outgoing = Array.from(this.children).filter(
						(c): c is HTMLElement => !(c instanceof SchmancyRoute) && c !== element,
					)

					// Set once this swap actually appends `element` — `finalize` below
					// re-appends on convergence only when this is true, never for a
					// swap superseded before it ever mounted.
					let mounted = false

					// A Web Animation as an Observable: subscribing is what starts it
					// (.animate() runs inside the subscriber, not before), its value is
					// the animation's own `finished`, and unsubscribing cancels it — so
					// a superseding swap tears down this one's in-flight fade for free,
					// through the same switchMap that drops the rest of the swap.
					const fade$ = (
						el: HTMLElement,
						keyframes: Keyframe[],
						fadeDuration: number,
						easing: string,
					): Observable<void> =>
						fadeDuration <= 0
							? of(undefined)
							: new Observable<void>(subscriber => {
									el.style.willChange = 'opacity'
									const anim = el.animate(keyframes, { duration: fadeDuration, easing, fill: 'forwards' })
									const done = () => {
										subscriber.next()
										subscriber.complete()
									}
									anim.finished.then(done, done)
									return () => anim.cancel()
								})

					// Mounting fires `connectedCallback` — a route that asserts fullscreen
					// there can collapse a nav-rail grid track and reflow whatever's still
					// on screen, so this runs only once nothing else is visible to reflow:
					// on first mount, or once the outgoing page has fully faded out and
					// been dropped.
					const mountAndFadeIn = (fadeDuration: number) => {
						dropStale()
						this.appendChild(element)
						this.scrollTop = 0
						mounted = true
						this.mounted = { pageKey: carry.pageKey, pagePath: carry.pagePath, element }
						return fade$(element, [{ opacity: 0 }, { opacity: 1 }], fadeDuration, 'ease-in')
					}

					// The outgoing page fades out alone; the incoming one mounts only once
					// it's gone — cross-fading two different page layouts ghosts, one
					// page's grid and type showing through the other. The 40/60 split
					// mounts the incoming page early enough that chrome its entrance
					// drives (e.g. a nav rail's own collapse) starts with the swap
					// rather than after it.
					const fadeOutMs = Math.round(duration * 0.4)

					// No outgoing page → first mount (faster fade-in, matching prior feel).
					const swap$ =
						duration === 0 || outgoing.length === 0
							? mountAndFadeIn(duration === 0 ? 0 : duration > 100 ? Math.max(100, duration * 0.66) : duration)
							: forkJoin(
									outgoing.map(el => {
										// The page on its way out stops taking clicks the moment it
										// starts leaving — it is a picture of where the member just was.
										el.style.pointerEvents = 'none'
										return fade$(el, [{ opacity: 1 }, { opacity: 0 }], fadeOutMs, 'ease-out')
									}),
								).pipe(switchMap(() => mountAndFadeIn(duration - fadeOutMs)))

					return swap$.pipe(
						// A throw inside the swap (a bad keyframe, a history-write failure)
						// aborts THIS navigation only — never the area's whole nav stream.
						catchError(err => {
							console.error(`[${this.name}] Swap error:`, err)
							return EMPTY
						}),
						finalize(() => {
							// Converges however this inner ended (completed OR superseded
							// mid-flight): stale pages gone, `element` mounted and visible.
							// Fade teardown (above) already cancelled any in-flight animation.
							// A swap superseded before `mounted` was ever set never appended
							// its element — it leaves it unmounted; the superseding
							// navigation converges the DOM on its own.
							dropStale()
							if (mounted && this.isConnected && !element.isConnected) {
								this.appendChild(element)
								this.scrollTop = 0
								this.mounted = { pageKey: carry.pageKey, pagePath: carry.pagePath, element }
							}
							element.style.willChange = 'auto'
							element.style.opacity = ''
						}),
					)
				}),

				takeUntil(this.disconnecting),
			)
			.subscribe()

		// The startup route, resolved now that every arm above is listening — reads
		// top-to-bottom: explicit pending push wins, else the URL (encoded-state,
		// then a matching path segment), else `.default`.
		//
		// It is spoken here rather than as a `merge` source because a source emits
		// while `merge` is still subscribing the sources after it. A startup route
		// whose guard refused SYNCHRONOUSLY therefore dispatched its `redirect`
		// before `area.request` had an observer at all, and the `area.push` the
		// handler answered with landed on a Subject nobody was listening to: no
		// history write, nothing mounted, no error. Only a guard that answered
		// asynchronously — a session read, a `timer(0)` — outlived that window,
		// which is why every redirect that worked, worked by accident.
		const pending = area.consumePending(this.name)
		if (pending) {
			this.startup.next(pending)
			return
		}

		const segment = location.pathname.split('/').pop() || ''
		if (segment.includes('{') || segment.includes('%7B')) {
			try {
				const route = JSON.parse(decodeURIComponent(segment))?.[this.name]
				if (route) {
					this.startup.next({
						_source: 'initial',
						area: this.name,
						component: typeof route.when === 'string' && this.routeNamed(route.when) ? route.when : route.component,
						state: route.state || {},
						params: route.params || {},
						props: route.props || {},
						historyStrategy: HISTORY_STRATEGY.replace,
					})
					return
				}
			} catch {
				/* malformed encoded segment — fall through */
			}
		}

		const addressed = this.routeAt(location.pathname)
		if (addressed) {
			// An address reached by a retired name moves to the route's current name.
			const renamed = addressed.renamed && `${addressed.renamed}${location.search}${location.hash}`
			this.startup.next({
				_source: 'initial',
				area: this.name,
				component: addressed.when,
				state: {},
				params: addressed.params,
				path: renamed,
				historyStrategy: renamed ? HISTORY_STRATEGY.replace : HISTORY_STRATEGY.silent,
			})
			return
		}

		if (this.default)
			this.startup.next({
				_source: 'initial',
				area: this.name,
				component: this.default,
				state: {},
				params: {},
				historyStrategy: HISTORY_STRATEGY.silent,
			})
	}

	/**
	 * The declared route `pathname` names for this area, with what its pattern
	 * captured. Inside a scope, the segments right after it name the route — an
	 * exact name first, then patterns in declaration order — so an id above the
	 * scope that happens to spell a route name is never read as one. With no scope,
	 * the first segment anywhere equal to an exact name wins, else a pattern
	 * matching the whole path.
	 */
	private routeAt(
		pathname: string,
	): { when: string; params: Record<string, string>; renamed?: string } | null {
		const segments = pathname.split('/').filter(Boolean)
		const scope = this.scopeStart(segments)
		if (scope === 'off-path') return null
		const routes = this.routes ?? []
		// A match under an alias names the address the route's current name gives
		// the same span: the alias's matched segments replaced by the route's
		// pattern filled in from what the alias captured. An alias that leaves a
		// param the pattern needs uncaptured cannot stand for the route at all.
		const named = (route: SchmancyRoute, name: string, params: Record<string, string>, from: number, length: number) => {
			if (name === route.when) return { when: route.when, params }
			if (!patternParamNames(route.when).every(param => param in params)) return null
			const built = buildPathFromPattern(route.when, params)
			const renamed =
				built === null
					? undefined
					: `/${[...segments.slice(0, from), built.slice(1), ...segments.slice(from + length)].join('/')}`
			return { when: route.when, params, renamed }
		}
		if (scope === 'whole-path') {
			for (let at = 0; at < segments.length; at += 1) {
				for (const route of routes.filter(route => !isDynamicRoutePattern(route.when))) {
					for (const name of [route.when, ...route.aliases]) {
						if (isDynamicRoutePattern(name) || segments[at] !== name) continue
						const found = named(route, name, {}, at, 1)
						if (found) return found
					}
				}
			}
			for (const route of routes.filter(route => isDynamicRoutePattern(route.when))) {
				for (const name of [route.when, ...route.aliases]) {
					const params = matchPathPattern(name, pathname)
					const found = params && named(route, name, params, 0, segments.length)
					if (found) return found
				}
			}
			return null
		}
		const tail = segments.slice(scope)
		for (const route of [
			...routes.filter(route => !isDynamicRoutePattern(route.when)),
			...routes.filter(route => isDynamicRoutePattern(route.when)),
		]) {
			for (const name of [route.when, ...route.aliases]) {
				const match = matchPathPrefix(name, tail)
				const found = match && named(route, name, match.params, scope, match.length)
				if (found) return found
			}
		}
		return null
	}

	/** The declared route answering to `name`: its `when`, or one of its aliases. */
	private routeNamed(name: string): SchmancyRoute | undefined {
		return this.routes?.find(route => route.when === name || route.aliases.includes(name))
	}

	/**
	 * How many leading path segments sit above this area: its `base`, else what its
	 * parent area's route accounts for. `whole-path` when neither applies — no parent,
	 * or a parent whose route the address does not carry — and `off-path` when the
	 * address does not begin with the declared base. @internal
	 */
	scopeStart(segments: string[]): number | 'whole-path' | 'off-path' {
		if (this.base) return matchPathPrefix(this.base, segments)?.length ?? 'off-path'
		for (
			let node: Node | null = this.parentNode;
			node;
			node = node.parentNode ?? (node instanceof ShadowRoot ? node.host : null)
		) {
			if (node instanceof SchmancyArea) return node.consumedThrough(segments) ?? 'whole-path'
		}
		return 'whole-path'
	}

	/** How many leading path segments this area and its mounted route account for,
	 *  or null when the address does not carry that route. @internal */
	consumedThrough(segments: string[]): number | null {
		const when = area.current.get(this.name)?.when
		if (!when) return null
		const scope = this.scopeStart(segments)
		if (scope === 'off-path') return null
		if (scope === 'whole-path') {
			if (isDynamicRoutePattern(when)) return matchPathPattern(when, `/${segments.join('/')}`) ? segments.length : null
			const at = segments.findIndex(segment => this.routeNamed(segment)?.when === when)
			return at < 0 ? null : at + 1
		}
		for (const name of [when, ...(this.routeNamed(when)?.aliases ?? [])]) {
			const match = matchPathPrefix(name, segments.slice(scope))
			if (match) return scope + match.length
		}
		return null
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		this.mounted = null
		area.pop(this.name)
	}

	render() {
		return html`<slot></slot>`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-area': SchmancyArea
	}
}
